// Header.jsx
import React, { useState } from "react";
import { Dropdown } from 'primereact/dropdown';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import { useUser } from '../../context/user';
import { getTabObject } from "../../helpers/common";
import { changePassword, logoutUser } from "../../api/methods";
import { USERNAME, ROLE, PERMISSION } from "../../constants/common";
import ChangePasswordForm from "../ChangePasswordForm/ChangePasswordForm";
import profileImg from '../../assets/img/profile.png';

import './Header.styles.scss';

/**
 * Header component providing navigation title and user profile functionality
 * Includes dropdown menu with logout and change password options
 */
const Header = ({ tab }) => {
    const { user, dispatchUser } = useUser();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [showPasswordChangeForm, setShowPasswordChangeForm] = useState(false);
    
    const userMenuOptions = [
        { name: t('Change Password'), key: "change" },
        { name: t('Logout'), key: "logout" }
    ];

    const handleLogout = async () => {
        try {
            // Call backend logout API to invalidate session
            await logoutUser();
        } catch (error) {
            console.error('Logout API call failed:', error);
            // Continue with client-side logout even if API call fails
        } finally {
            // Clear user context
            dispatchUser({
                type: 'SET_USER',
                payload: null,
            });

            // Clear all stored user data
            localStorage.removeItem(USERNAME);
            localStorage.removeItem(ROLE);
            localStorage.removeItem(PERMISSION);
            sessionStorage.removeItem(USERNAME);

            // Redirect to login page
            navigate("/login");
        }
    };

    const handlePasswordChange = async (currentPassword, newPassword) => {
        try {
            const requestData = {
                currentPassword,
                newPassword,
            };

            const response = await changePassword(requestData);

            if (response === "success") {
                setShowPasswordChangeForm(false); 
                // Use a toast notification system instead of alert for a more professional look
                alert(t("Password changed successfully!"));
            } else {
                alert(t("Password change failed"));
            }
        } catch (error) {
            console.error('Password change failed:', error);
            alert(t("An error occurred while changing your password"));
        }
    };

    const handleMenuAction = (e) => {
        const key = e.value;
        switch (key) {
            case "logout":
                handleLogout();
                break;
            case "change":
                setShowPasswordChangeForm(true);
                break;
            default:
                console.log("Unknown action:", key);
        }
    };

    const getCurrentPageTitle = () => {
        const tabObject = getTabObject(tab);
        return tabObject ? t(tabObject.label) : '';
    };

    return (
        <header className="app-header">
            <h1 className="app-header__title">{getCurrentPageTitle()}</h1>
            
            {user && (
                <div className="app-header__user-profile">
                    <span className="app-header__username">{user.userName}</span>
                    
                    <Dropdown 
                        className="app-header__dropdown"
                        onChange={handleMenuAction} 
                        options={userMenuOptions} 
                        optionLabel="name" 
                        optionValue="key"
                        aria-label={t("User menu")}
                    />
                    
                    <div className="app-header__avatar">
                        <img src={profileImg} alt={t("Profile")} />
                    </div>
                </div>
            )}
            
            {showPasswordChangeForm && (
                <>
                    <div className="app-header__overlay" onClick={() => setShowPasswordChangeForm(false)}></div>
                    <ChangePasswordForm 
                        onChangePassword={handlePasswordChange} 
                        onCancel={() => setShowPasswordChangeForm(false)} 
                    />
                </>
            )}
        </header>
    );
};

Header.propTypes = {
    tab: PropTypes.string
};

export default React.memo(Header);